import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { TagCategory } from '@leedpulse-monorepo/enums';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import { CompanyContact, ContactSubscription, ContactTag } from '../../../../domain/models/Contact';

dayjs.locale('fr');

export type ContactsSortBy = 'alphabetical' | 'recent' | 'heat' | 'type';
export type ContactsLastInteraction = 'any' | 'today' | '7_days' | '30_days' | 'more_than_30_days';
export type ContactHeatLevel = 'hot' | 'warm' | 'cold';

export interface ContactsFilters {
  search: string;
  subscriptionId: string | null;
  sortBy: ContactsSortBy;
  heat: string[];
  types: string[];
  tags: string[];
  lastInteraction: ContactsLastInteraction;
}

// Chosen value of a dropdown filter, shown under the filters to be removed one by one
export interface ContactsFilterChip {
  key: 'heat' | 'types' | 'tags' | 'lastInteraction';
  value: string;
  label: string;
}

export const DEFAULT_CONTACTS_FILTERS: ContactsFilters = {
  search: '',
  subscriptionId: null,
  sortBy: 'alphabetical',
  heat: [],
  types: [],
  tags: [],
  lastInteraction: 'any',
};

export const CONTACTS_SORT_OPTIONS: { value: ContactsSortBy; label: string }[] = [
  { value: 'recent', label: 'Récent' },
  { value: 'alphabetical', label: 'A-Z' },
  { value: 'heat', label: 'Chaleur' },
  { value: 'type', label: 'Type' },
];

export const CONTACTS_LAST_INTERACTION_OPTIONS: { value: ContactsLastInteraction; label: string }[] = [
  { value: 'any', label: 'Peu importe' },
  { value: 'today', label: "Aujourd'hui" },
  { value: '7_days', label: '7 derniers jours' },
  { value: '30_days', label: '30 derniers jours' },
  { value: 'more_than_30_days', label: 'Plus de 30 jours' },
];

export const CONTACTS_NAMELESS_LETTER = '#';
export const CONTACTS_NO_HEAT_LABEL = 'Sans chaleur';
export const CONTACTS_NO_TYPE_LABEL = 'Sans type';

export const CONTACT_HEAT_LABELS: Record<ContactHeatLevel, string> = { hot: 'Chaud', warm: 'Tiède', cold: 'Froid' };
const HEAT_RANK: Record<ContactHeatLevel, number> = { hot: 0, warm: 1, cold: 2 };

export interface ContactDisplay {
  id: string;
  subscriptionId: string;
  fullName: string;
  sortName: string;
  initials: string;
  phone: string;
  // As stored, the key of the contact's conversation
  rawPhone: string;
  email: string;
  company: string;
  role: string;
  address: string;
  website: string;
  summaryMarkdown: string;
  heat: ContactTag | null;
  heatLevel: ContactHeatLevel | null;
  status: ContactTag | null;
  typologies: ContactTag[];
  customTags: ContactTag[];
  followedBy: string;
  isOwn: boolean;
  // No AI analysis is generated for a confidential contact
  isConfidential: boolean;
  lastInteraction: number;
  lastInteractionLabel: string;
  lastActivity: CompanyContact['last_activity'] | null;
  created: number;
  updated: number;
}

export interface ContactsLetterGroup {
  // A letter, or a heat / type label when sorted by heat / type
  letter: string;
  isLabel?: boolean;
  heatLevel?: ContactHeatLevel | null;
  contacts: ContactDisplay[];
}

function toTimestamp(value: number | string | undefined): number {
  if (value === undefined || value === null || value === '') return 0;
  return typeof value === 'number' ? value : dayjs(value).valueOf() || 0;
}

function getInitials(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter((part) => /^\p{L}/u.test(part))
    .map((part) => part[0].toUpperCase());
  return letters.slice(0, 2).join('') || '?';
}

// The heat tags are named by the domain ("Chaud", "Chauds", "Tiède"...): their level comes from the name
function getHeatLevel(tag: ContactTag | null): ContactHeatLevel | null {
  const name = tag?.name.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase() ?? '';
  if (name.startsWith('chaud')) return 'hot';
  if (name.startsWith('tied')) return 'warm';
  if (name.startsWith('froid')) return 'cold';
  return null;
}

export function formatContactLastInteraction(timestamp: number, now: Dayjs = dayjs()): string {
  if (!timestamp) return '';
  const days = now.startOf('day').diff(dayjs(timestamp).startOf('day'), 'day');
  if (days <= 0) return "Aujourd'hui";
  if (days === 1) return 'Hier';
  return `Il y a ${days} jours`;
}

// Label of the user's own line, in the list and in the line picker
export function formatOwnLineLabel(userName: string): string {
  return `Ma ligne${userName ? ` · ${userName.split(' ')[0]}` : ''}`;
}

// Line picker: the whole company, the user's own line, then the others in alphabetical order
export function buildSubscriptionOptions(
  subscriptions: ContactSubscription[],
  userNameBySubscription: Map<string, string>,
  allLabel: string,
): { value: string; label: string }[] {
  const lines = subscriptions.map((subscription) => {
    const userName = userNameBySubscription.get(subscription.id) ?? '';
    const label = subscription.is_own ? formatOwnLineLabel(userName) : userName || subscription.name || 'Non assigné';
    return { value: subscription.id, label, isOwn: subscription.is_own };
  });
  lines.sort((a, b) => Number(b.isOwn) - Number(a.isOwn) || a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  return [{ value: '', label: allLabel }, ...lines.map(({ value, label }) => ({ value, label }))];
}

export function buildContactDisplay(
  contact: CompanyContact,
  tagsById: Map<string, ContactTag>,
  subscriptions: ContactSubscription[],
  userNameBySubscription: Map<string, string>,
  now: Dayjs = dayjs(),
): ContactDisplay {
  const fields = contact.contact ?? {};
  const name = [fields.firstname, fields.lastname].map((part) => part?.trim()).filter(Boolean).join(' ');
  const phone = fields.phone ? formatPhoneNumber(fields.phone) : '';
  const tags = Object.keys(contact.tags ?? {})
    .map((tagId) => tagsById.get(tagId))
    .filter((tag): tag is ContactTag => !!tag);
  const heat = tags.find((tag) => tag.category === TagCategory.heat) ?? null;

  const isOwn = !!subscriptions.find((subscription) => subscription.id === contact.subscription_id)?.is_own;
  const userName = userNameBySubscription.get(contact.subscription_id) ?? '';
  const followedBy = isOwn ? formatOwnLineLabel(userName) : userName || 'Non assigné';

  const created = toTimestamp(contact.created);
  const updated = toTimestamp(contact.updated) || created;
  const lastInteraction = contact.last_activity?.timestamp || updated;

  return {
    id: contact.id,
    subscriptionId: contact.subscription_id,
    fullName: name || phone || 'Contact sans nom',
    sortName: (fields.lastname?.trim() || fields.firstname?.trim() || '').toLowerCase(),
    initials: getInitials(name),
    phone,
    rawPhone: fields.phone ?? '',
    // 'not_available' marks a contact stored without email, like on mobile (ContactInfoTab)
    email: fields.email && fields.email !== 'not_available' ? fields.email : '',
    company: fields.company ?? '',
    role: fields.role ?? '',
    address: fields.adresse ?? '',
    website: fields.website ?? '',
    summaryMarkdown: fields.details?.trim() ?? '',
    heat,
    heatLevel: getHeatLevel(heat),
    status: tags.find((tag) => tag.category === TagCategory.status) ?? null,
    typologies: tags.filter((tag) => tag.category === TagCategory.typology),
    customTags: tags.filter((tag) => tag.category === TagCategory.custom),
    followedBy,
    isOwn,
    isConfidential: !!fields.confidential,
    lastInteraction,
    lastInteractionLabel: formatContactLastInteraction(lastInteraction, now),
    lastActivity: contact.last_activity ?? null,
    created,
    updated,
  };
}

// First letter of the last name, else of the first name (LEED-2260), accents dropped
export function getContactLetter(contact: ContactDisplay): string {
  const firstChar = contact.sortName.normalize('NFD').replace(/\p{M}/gu, '').charAt(0).toUpperCase();
  return /\p{L}/u.test(firstChar) ? firstChar : CONTACTS_NAMELESS_LETTER;
}

// Keeps the order received; the `#` section (contacts without a name) goes last (LEED-2310)
export function groupContactsByLetter(contacts: ContactDisplay[]): ContactsLetterGroup[] {
  const groups = new Map<string, ContactDisplay[]>();
  contacts.forEach((contact) => {
    const letter = getContactLetter(contact);
    groups.set(letter, [...(groups.get(letter) ?? []), contact]);
  });
  return [...groups.entries()]
    .map(([letter, groupContacts]) => ({ letter, contacts: groupContacts }))
    .sort((a, b) => Number(a.letter === CONTACTS_NAMELESS_LETTER) - Number(b.letter === CONTACTS_NAMELESS_LETTER));
}

// Keeps the order received (sortContactDisplays 'heat'): one group per level, contacts without heat last
export function groupContactsByHeat(contacts: ContactDisplay[]): ContactsLetterGroup[] {
  const groups = new Map<ContactHeatLevel | null, ContactDisplay[]>();
  contacts.forEach((contact) => {
    groups.set(contact.heatLevel, [...(groups.get(contact.heatLevel) ?? []), contact]);
  });
  return [...groups.entries()].map(([heatLevel, groupContacts]) => ({
    letter: heatLevel ? CONTACT_HEAT_LABELS[heatLevel] : CONTACTS_NO_HEAT_LABEL,
    isLabel: true,
    heatLevel,
    contacts: groupContacts,
  }));
}

// Keeps the order received (sortContactDisplays 'type'): one group per first typology, contacts without one last
export function groupContactsByType(contacts: ContactDisplay[]): ContactsLetterGroup[] {
  const groups = new Map<string, ContactDisplay[]>();
  contacts.forEach((contact) => {
    const label = contact.typologies[0]?.name || CONTACTS_NO_TYPE_LABEL;
    groups.set(label, [...(groups.get(label) ?? []), contact]);
  });
  return [...groups.entries()].map(([label, groupContacts]) => ({ letter: label, isLabel: true, contacts: groupContacts }));
}

// The list renders the first N contacts only: thousands of rows at once make the page sluggish
export function limitContactGroups(groups: ContactsLetterGroup[], count: number): ContactsLetterGroup[] {
  let remaining = count;
  return groups
    .map((group) => {
      const contacts = group.contacts.slice(0, Math.max(remaining, 0));
      remaining -= contacts.length;
      return { ...group, contacts };
    })
    .filter((group) => group.contacts.length > 0);
}

// Contacts to render so that a letter of the index is in the list
export function countContactsThroughLetter(groups: ContactsLetterGroup[], letter: string): number {
  const index = groups.findIndex((group) => group.letter === letter);
  if (index === -1) return 0;
  return groups.slice(0, index + 1).reduce((total, group) => total + group.contacts.length, 0);
}

export function matchesLastInteraction(timestamp: number, lastInteraction: ContactsLastInteraction, now: Dayjs): boolean {
  switch (lastInteraction) {
    case 'today':
      return dayjs(timestamp).isSame(now, 'day');
    case '7_days':
      return timestamp >= now.subtract(7, 'day').valueOf();
    case '30_days':
      return timestamp >= now.subtract(30, 'day').valueOf();
    case 'more_than_30_days':
      return timestamp < now.subtract(30, 'day').valueOf();
    default:
      return true;
  }
}

export function filterContactDisplays(contacts: ContactDisplay[], filters: ContactsFilters, now: Dayjs = dayjs()): ContactDisplay[] {
  const query = filters.search.trim().toLowerCase();
  const digitsQuery = query.replace(/[\s.+]/g, '');

  return contacts.filter((contact) => {
    if (filters.subscriptionId && contact.subscriptionId !== filters.subscriptionId) return false;
    if (filters.heat.length > 0 && !filters.heat.includes(contact.heat?.id ?? '')) return false;
    if (filters.types.length > 0 && !contact.typologies.some((tag) => filters.types.includes(tag.id))) return false;
    if (filters.tags.length > 0 && !contact.customTags.some((tag) => filters.tags.includes(tag.id))) return false;
    if (!matchesLastInteraction(contact.lastInteraction, filters.lastInteraction, now)) return false;
    if (!query) return true;

    const tagNames = [contact.heat, contact.status, ...contact.typologies, ...contact.customTags].map((tag) => tag?.name ?? '');
    const matchesText = [contact.fullName, contact.company, contact.email, ...tagNames].some((value) =>
      value.toLowerCase().includes(query),
    );
    return matchesText || (!!digitsQuery && contact.phone.replace(/[\s.+]/g, '').includes(digitsQuery));
  });
}

function compareByName(a: ContactDisplay, b: ContactDisplay): number {
  // Contacts without a name go after the named ones, like the backend order (LEED-2310)
  if (!a.sortName !== !b.sortName) return a.sortName ? -1 : 1;
  return a.sortName.localeCompare(b.sortName, 'fr');
}

export function sortContactDisplays(contacts: ContactDisplay[], sortBy: ContactsSortBy): ContactDisplay[] {
  const byName = [...contacts].sort(compareByName);
  if (sortBy === 'recent') return byName.sort((a, b) => b.lastInteraction - a.lastInteraction);
  if (sortBy === 'heat') {
    const rank = (contact: ContactDisplay) => (contact.heatLevel ? HEAT_RANK[contact.heatLevel] : Object.keys(HEAT_RANK).length);
    return byName.sort((a, b) => rank(a) - rank(b));
  }
  if (sortBy === 'type') {
    // Contacts without a typology go last
    const typeName = (contact: ContactDisplay) => contact.typologies[0]?.name ?? '';
    return byName.sort((a, b) => {
      if (!typeName(a) !== !typeName(b)) return typeName(a) ? -1 : 1;
      return typeName(a).localeCompare(typeName(b), 'fr');
    });
  }
  return byName;
}
